/**
 * Game Balance Manager for Cell Swap Game
 * 
 * Manages difficulty scaling, scoring balance, and game progression
 * to ensure optimal gameplay experience across all difficulty levels.
 */

export interface DifficultySettings {
  fallSpeed: number
  spawnRates: CellSpawnRates
  scoreMultiplier: number
  specialEventChance: number
}

export interface CellSpawnRates {
  stem: number
  rbc: number
  wbc: number
  donor: number
  cancer: number
}

export interface ScoreBalanceConfig {
  baseMatchScore: number
  chainMultipliers: number[]
  sizeMultipliers: number[]
  cellTypeMultipliers: { [key: string]: number }
}

export interface BalanceMetrics {
  averageMatchesPerMinute: number
  averageScorePerMatch: number
  currentDifficulty: number
  timeToNextLevel: number
}

export class GameBalanceManager {
  private static readonly MAX_MATCH_HISTORY = 100

  private static readonly DIFFICULTY_CURVES = {
    // Fall speed progression (ms per fall)
    fallSpeed: [1200, 1100, 1000, 900, 800, 750, 700, 650, 600, 550],

    // Score multipliers per level
    scoreMultipliers: [1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 2.0],

    // Cancer spawn rate increase over levels
    cancerRates: [0.05, 0.08, 0.10, 0.12, 0.15, 0.18, 0.20, 0.22, 0.25, 0.28]
  }

  private static readonly BASE_SPAWN_RATES: CellSpawnRates = {
    stem: 0.15,    // 15% - Valuable but not too common
    rbc: 0.25,     // 25% - Common healthy cell
    wbc: 0.20,     // 20% - Important for cancer clearing
    donor: 0.25,   // 25% - Common, synergizes with stem
    cancer: 0.15   // 15% - Challenge element, varies by level
  }

  private static readonly SCORE_CONFIG: ScoreBalanceConfig = {
    baseMatchScore: 100,
    chainMultipliers: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 6.0, 8.0, 10.0],
    sizeMultipliers: [1.0, 1.2, 1.5, 2.0, 2.5, 3.0, 4.0],  // 4, 5, 6, 7, 8, 9, 10+ cells
    cellTypeMultipliers: {
      STEM: 1.0,      // Stem cells worth double
      DONOR: 1.0,     // Donor cells valuable
      WBC: 1.0,       // WBC slightly more valuable
      RBC: 1.0,       // RBC baseline
      CANCER: 1.0     // Cancer cells penalized when matched normally
    },
  }

  private currentLevel: number = 1
  private gameStartTime: number = 0
  private matchHistory: Array<{
    timestamp: number
    score: number
    chainLevel: number
    cellsCleared: number
  }> = []
  private balanceMetrics: BalanceMetrics = {
    averageMatchesPerMinute: 0,
    averageScorePerMatch: 0,
    currentDifficulty: 0,
    timeToNextLevel: 0
  }

  constructor() {
    this.gameStartTime = Date.now()
  }

  /**
   * Gets difficulty settings for the current level
   */
  public getDifficultySettings(level: number): DifficultySettings {
    this.currentLevel = Math.max(1, Math.min(10, level))

    const spawnRates = this.calculateSpawnRates(this.currentLevel)

    return {
      fallSpeed: GameBalanceManager.DIFFICULTY_CURVES.fallSpeed[this.currentLevel],
      spawnRates,
      scoreMultiplier: GameBalanceManager.DIFFICULTY_CURVES.scoreMultipliers[this.currentLevel],
      specialEventChance: Math.min(0.1 + (this.currentLevel * 0.02), 0.3) // 10% to 30%
    }
  }

  /**
   * Calculates balanced spawn rates for cell types based on level
   */
  public calculateSpawnRates(level: number): CellSpawnRates {
    const base = { ...GameBalanceManager.BASE_SPAWN_RATES }
    const cancerIncrease = GameBalanceManager.DIFFICULTY_CURVES.cancerRates[level] - base.cancer

    // Increase cancer rate and adjust others proportionally
    base.cancer = GameBalanceManager.DIFFICULTY_CURVES.cancerRates[level]

    // Slightly increase WBC to help deal with cancer
    base.wbc = Math.min(0.25, base.wbc + (cancerIncrease * 0.3))

    // Decrease RBC proportionally to maintain balance
    base.rbc = Math.max(0.15, base.rbc - (cancerIncrease * 0.5))

    // Normalize to ensure total is 1.0
    const total = Object.values(base).reduce((sum, rate) => sum + rate, 0)
    const normalizeFactor = 1.0 / total

    return {
      stem: base.stem * normalizeFactor,
      rbc: base.rbc * normalizeFactor,
      wbc: base.wbc * normalizeFactor,
      donor: base.donor * normalizeFactor,
      cancer: base.cancer * normalizeFactor
    }
  }

  /**
   * Calculate dynamic fall speed based on level
   */
  public calculateFallSpeed(level: number): number {
    const clampedLevel = Math.max(0, Math.min(level, GameBalanceManager.DIFFICULTY_CURVES.fallSpeed.length - 1))
    return GameBalanceManager.DIFFICULTY_CURVES.fallSpeed[clampedLevel]
  }

  /**
   * Record match result for balance analysis
   */
  public recordMatchResult(matchData: {
    score: number
    chainLevel: number
    cellsCleared: number
    level: number
  }): void {
    this.matchHistory.push({
      timestamp: Date.now(),
      score: matchData.score,
      chainLevel: matchData.chainLevel,
      cellsCleared: matchData.cellsCleared
    })

    // Keep only recent matches for analysis
    if (this.matchHistory.length > GameBalanceManager.MAX_MATCH_HISTORY) {
      this.matchHistory.shift()
    }
  }

  /**
   * Calculates balanced score for a match based on current settings
   */
  public calculateMatchScore(
    cellTypes: string[],
    matchSize: number,
    chainLevel: number = 0
  ): number {
    const config = GameBalanceManager.SCORE_CONFIG
    const settings = this.getDifficultySettings(this.currentLevel)

    // Base score calculation
    let baseScore = config.baseMatchScore

    // Apply size multiplier
    const sizeIndex = Math.min(matchSize - 4, config.sizeMultipliers.length - 1)
    baseScore *= config.sizeMultipliers[Math.max(0, sizeIndex)]

    // Apply cell type multipliers
    let typeMultiplier = 1.0
    const uniqueTypes = new Set(cellTypes)
    for (const cellType of uniqueTypes) {
      typeMultiplier *= (config.cellTypeMultipliers[cellType] || 1.0)
    }
    baseScore *= typeMultiplier

    // Apply chain multiplier
    const chainIndex = Math.min(chainLevel, config.chainMultipliers.length - 1)
    baseScore *= config.chainMultipliers[chainIndex]

    // Apply difficulty score multiplier
    baseScore *= settings.scoreMultiplier

    return Math.floor(baseScore)
  }

  /**
   * Records a match for balance analysis
   */
  public recordMatch(score: number, chainLevel: number = 0, cellsCleared: number = 0): void {
    this.matchHistory.push({
      timestamp: Date.now(),
      score,
      chainLevel,
      cellsCleared
    })

    // Keep only recent matches (last 5 minutes)
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
    this.matchHistory = this.matchHistory.filter(match => match.timestamp >= fiveMinutesAgo)

    this.updateBalanceMetrics()
  }

  /**
   * Updates balance metrics for analysis
   */
  private updateBalanceMetrics(): void {
    if (this.matchHistory.length === 0) return

    const gameTimeMinutes = (Date.now() - this.gameStartTime) / (60 * 1000)
    const totalMatches = this.matchHistory.length

    this.balanceMetrics = {
      averageMatchesPerMinute: totalMatches / Math.max(1, gameTimeMinutes),
      averageScorePerMatch: this.matchHistory.reduce((sum, m) => sum + m.score, 0) / totalMatches,
      currentDifficulty: this.currentLevel + 1,
      timeToNextLevel: this.estimateTimeToNextLevel()
    }
  }

  /**
   * Estimates time to reach next level
   */
  private estimateTimeToNextLevel(): number {
    if (this.balanceMetrics.averageScorePerMatch <= 0 ||
      this.balanceMetrics.averageMatchesPerMinute <= 0) {
      return 0
    }

    return 0 // Time-based levels don't depend on match rate
  }

  /**
   * Gets current balance metrics for monitoring
   */
  public getBalanceMetrics(): BalanceMetrics {
    return { ...this.balanceMetrics }
  }

  /**
   * Provides recommendations for balance adjustments
   */
  public getBalanceRecommendations(): string[] {
    const recommendations: string[] = []
    const metrics = this.balanceMetrics

    if (metrics.averageMatchesPerMinute > 8) {
      recommendations.push("Consider increasing fall speed - matches happening too frequently")
    }

    if (metrics.averageMatchesPerMinute < 3) {
      recommendations.push("Consider decreasing fall speed - gameplay may feel too slow")
    }

    if (metrics.averageScorePerMatch < 500) {
      recommendations.push("Consider increasing score multipliers - scores may feel unrewarding")
    }

    if (metrics.timeToNextLevel > 10) {
      recommendations.push("Level progression may be too slow for engagement")
    }

    return recommendations
  }

  /**
   * Gets debug information for balance tuning
   */
  public getDebugInfo(): any {
    return {
      currentLevel: this.currentLevel,
      difficultySettings: this.getDifficultySettings(this.currentLevel),
      balanceMetrics: this.balanceMetrics,
      recentMatches: this.matchHistory.slice(-10),
      recommendations: this.getBalanceRecommendations()
    }
  }
}